// src/pages/Sucursales.js
//
// Lista de sucursales de la empresa activa.
// - Tarjetas con nombre, código, dirección, teléfono y chips de
//   presencia (HolaVeci / POS / Inactiva) según flags EnHolaVeci
//   y EnPosCompacto (migración 034).
// - Botón "Nueva sucursal" abre dialog para crearla. Se crea SIEMPRE
//   con EnPosCompacto=true (viene del POS Compacto) y EnHolaVeci=false.
//   Si el tendero quiere activarla en HolaVeci, lo hace desde el
//   editor de sucursal (C3.3).
// - "Operar esta sucursal" cambia sucursalActiva del AppContext y
//   lleva al dashboard operativo.

import React, { useState } from 'react';
import {
  Box, Grid, Card, CardContent, CardActions, Typography, Stack, Button, IconButton,
  Chip, Alert, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Tooltip, Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PlaceIcon from '@mui/icons-material/Place';
import PhoneIcon from '@mui/icons-material/Phone';
import EditIcon from '@mui/icons-material/Edit';
import LaunchIcon from '@mui/icons-material/Launch';
import StarIcon from '@mui/icons-material/Star';
import { useApp } from '../context/AppContext';
import { sucursales as apiSucursales } from '../api/posApi';
import { useNavigate } from 'react-router-dom';

const Sucursales = () => {
  const {
    empresa, sucursales, refrescarEmpresa, notify,
    seleccionarSucursal,
  } = useApp();
  const navigate = useNavigate();
  const [nuevaAbierta, setNuevaAbierta] = useState(false);

  const empresaId = empresa?.empresaId || empresa?.EmpresaId;
  const nombreEmpresa = empresa?.nombreComercial || empresa?.NombreComercial
                    || empresa?.razonSocial || empresa?.RazonSocial || '—';

  if (!empresa) {
    return (
      <Alert severity="info">
        Selecciona una empresa desde el header para ver sus sucursales.
      </Alert>
    );
  }

  const operar = (sucursalId) => {
    seleccionarSucursal(sucursalId);
    navigate('/dashboard');
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center"
             sx={{ mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <StorefrontIcon color="primary" fontSize="large" />
          <Box>
            <Typography variant="h4" fontWeight={700}>Sucursales</Typography>
            <Typography variant="body2" color="text.secondary">
              Puntos de venta y operación de <b>{nombreEmpresa}</b>
            </Typography>
          </Box>
        </Stack>
        <Button variant="contained" startIcon={<AddIcon />}
                onClick={() => setNuevaAbierta(true)}>
          Nueva sucursal
        </Button>
      </Stack>

      {sucursales.length === 0 ? (
        <Alert severity="info" sx={{ mt: 3 }}>
          Esta empresa aún no tiene sucursales. Crea la primera con el botón <b>Nueva sucursal</b>.
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {sucursales.map((s) => {
            const id       = s.sucursalId || s.SucursalId;
            const nombre   = s.nombre || s.Nombre;
            const codigo   = s.codigo || s.Codigo;
            const direccion = s.direccion || s.Direccion;
            const telefono = s.telefono || s.Telefono;
            const esPrincipal = s.esPrincipal ?? s.EsPrincipal ?? false;
            const activa  = s.activa ?? s.Activa ?? true;
            const enHV    = s.enHolaVeci    ?? s.EnHolaVeci    ?? false;
            const enPOS   = s.enPosCompacto ?? s.EnPosCompacto ?? false;

            return (
              <Grid item xs={12} md={6} lg={4} key={id}>
                <Card sx={{
                  height: '100%', display: 'flex', flexDirection: 'column',
                  borderLeft: 4, borderColor:
                    !activa            ? 'grey.400'    :
                    enHV && enPOS      ? 'success.main':
                    enHV               ? 'info.main'   :
                    enPOS              ? 'primary.main':
                                         'grey.400',
                }}>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                      <Typography variant="h6" fontWeight={600} noWrap sx={{ flexGrow: 1 }}>
                        {nombre}
                      </Typography>
                      {esPrincipal && (
                        <Tooltip title="Sucursal principal">
                          <StarIcon fontSize="small" color="warning" />
                        </Tooltip>
                      )}
                    </Stack>

                    <Chip size="small" label={codigo} variant="outlined"
                          sx={{ height: 20, fontSize: 10, mb: 1.5 }} />

                    <Stack direction="row" spacing={0.5} sx={{ mb: 1.5, flexWrap: 'wrap', gap: 0.5 }}>
                      {enHV  && <Chip size="small" label="HolaVeci" color="info"    variant="filled" />}
                      {enPOS && <Chip size="small" label="Local"    color="primary" variant="filled" />}
                      {!enHV && !enPOS && (
                        <Chip size="small" label="Sin actividad" variant="outlined" />
                      )}
                      {!activa && <Chip size="small" label="Inactiva" color="default" />}
                    </Stack>

                    {direccion && (
                      <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 0.5 }}>
                        <PlaceIcon fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                          {direccion}
                        </Typography>
                      </Stack>
                    )}
                    {telefono && (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <PhoneIcon fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                          {telefono}
                        </Typography>
                      </Stack>
                    )}
                  </CardContent>
                  <Divider />
                  <CardActions sx={{ justifyContent: 'space-between' }}>
                    <Tooltip title="Editar sucursal">
                      <IconButton size="small" onClick={() => navigate(`/sucursales/${id}`)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Button size="small" startIcon={<LaunchIcon />}
                            onClick={() => operar(id)}
                            disabled={!enPOS || !activa}>
                      Operar
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      <DialogNuevaSucursal
        open={nuevaAbierta}
        empresaId={empresaId}
        sucursalesExistentes={sucursales}
        onClose={() => setNuevaAbierta(false)}
        onCreated={async () => {
          setNuevaAbierta(false);
          await refrescarEmpresa();
          notify('Sucursal creada.', 'success');
        }}
        onError={(m) => notify(m, 'error')}
      />
    </Box>
  );
};

// ═════════════════════════════════════════════════════════════
// Dialog crear sucursal
// ═════════════════════════════════════════════════════════════

function DialogNuevaSucursal({ open, empresaId, sucursalesExistentes, onClose, onCreated, onError }) {
  const [f, setF] = useState({ codigo: '', nombre: '', direccion: '', telefono: '', email: '' });
  const [guardando, setGuardando] = useState(false);

  const upd = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const submit = async () => {
    setGuardando(true);
    try {
      // Si es la primera sucursal, se marca como principal.
      const hayPrincipal = sucursalesExistentes.some(s => s.esPrincipal || s.EsPrincipal);
      await apiSucursales.crear({
        empresaId,
        codigo:        f.codigo.trim(),
        nombre:        f.nombre.trim(),
        nombreCorto:   null,
        esPrincipal:   !hayPrincipal,
        direccion:     f.direccion.trim(),
        municipioID:   null,
        telefono:      f.telefono.trim() || null,
        email:         f.email.trim() || null,
        latitud:       null,
        longitud:      null,
        // Sucursales creadas desde el POS Compacto entran solo al POS.
        // Para activarlas en HolaVeci se hace desde el editor.
        enHolaVeci:    false,
        enPosCompacto: true,
      });
      // Reset form
      setF({ codigo: '', nombre: '', direccion: '', telefono: '', email: '' });
      onCreated();
    } catch (e) {
      onError(e.mensajeUsuario || 'No se pudo crear la sucursal.');
    } finally { setGuardando(false); }
  };

  const puedeGuardar = f.codigo.trim() && f.nombre.trim() && f.direccion.trim();

  return (
    <Dialog open={open} onClose={() => !guardando && onClose()} fullWidth maxWidth="sm">
      <DialogTitle>Nueva sucursal</DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }} variant="outlined">
          Esta sucursal se crea para <b>HolaVeci Local</b>. Puedes activarla también
          en HolaVeci después, desde el editor de la sucursal.
        </Alert>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2}>
            <TextField label="Código" value={f.codigo} onChange={upd('codigo')}
                       required sx={{ width: 160 }}
                       helperText="Ej. SUC-001, PPAL, CAB" />
            <TextField label="Nombre" value={f.nombre} onChange={upd('nombre')}
                       required fullWidth
                       helperText="Ej. Cabecera, Cañaveral, La 40" />
          </Stack>
          <TextField label="Dirección" value={f.direccion} onChange={upd('direccion')}
                     required />
          <Stack direction="row" spacing={2}>
            <TextField label="Teléfono" value={f.telefono} onChange={upd('telefono')} fullWidth />
            <TextField label="Correo"   type="email"
                       value={f.email} onChange={upd('email')} fullWidth />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={guardando}>Cancelar</Button>
        <Button variant="contained" onClick={submit}
                disabled={guardando || !puedeGuardar}>
          {guardando ? 'Creando…' : 'Crear sucursal'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default Sucursales;
